import React from "react";
import { useTranslation } from "react-i18next";
import OfferCountdownTimer from "./OfferCountdownTimer";
import "./OfferCountdownTimer.css";

/**
 * ProductPriceDisplay Component
 * Displays product price with offer support
 * Shows selling price with original price crossed out when there's an active offer
 * 
 * @param {number} price - Original product price
 * @param {number|null} sellingPrice - Offer/selling price (null if no offer)
 * @param {boolean} hasCategoryOffer - Whether the product has an active category offer
 * @param {string} offerValidUntil - ISO 8601 date string for when the offer expires
 * @param {string} currencyLabel - Currency label to display
 * @param {string} priceLabel - Label text for price (e.g., "Price" or "السعر")
 * @param {boolean} showTimer - Whether to show the countdown timer
 * @param {boolean} compactTimer - Whether to use compact timer version
 * @param {string} lang - Current language (en/ar)
 */
const ProductPriceDisplay = ({
  price,
  sellingPrice,
  hasCategoryOffer,
  offerValidUntil,
  currencyLabel,
  priceLabel,
  showTimer = true,
  compactTimer = true,
  lang = "ar",
}) => {
  const [_] = useTranslation("global");
  
  // Check if offer is currently active
  const isOfferActive = () => {
    if (!hasCategoryOffer || !sellingPrice) return false;
    if (!offerValidUntil) return true; // If no end date, offer is always active
    
    const now = new Date();
    const endDate = new Date(offerValidUntil);
    return endDate > now;
  };

  const hasActiveOffer = isOfferActive();
  
  // Calculate discount percentage
  const calculateDiscount = () => {
    if (!hasActiveOffer || !sellingPrice || sellingPrice >= price) return 0;
    return Math.round(((price - sellingPrice) / price) * 100);
  };

  const discountPercentage = calculateDiscount();

  if (!hasActiveOffer) {
    // No offer - show regular price
    return (
      <div className={`px-1 py-1 d-flex ${lang === "en" ? "flex-row-reverse" : ""} justify-content-between gap-5`}>
        <p>{priceLabel}</p>
        <p>{price}{currencyLabel}</p>
      </div>
    );
  }

  // Has active offer - show selling price with original crossed out
  return (
    <div className="product-price-display">
      <div className={`px-1 py-1 d-flex ${lang === "en" ? "flex-row-reverse" : ""} justify-content-between align-items-center`}>
        <p>{priceLabel}</p>
        <div className={`offer-price-row ${lang === "en" ? "flex-row" : "flex-row-reverse"}`}>
          <span className="original-price">
            {price}{currencyLabel}
          </span>
          <span className="selling-price">
            {sellingPrice}{currencyLabel}
          </span>
          {discountPercentage > 0 && (
            <span className="discount-badge">
              -{discountPercentage}%
            </span>
          )}
        </div>
      </div>
      {showTimer && offerValidUntil && (
        <OfferCountdownTimer 
          offerValidUntil={offerValidUntil} 
          compact={compactTimer} 
        />
      )}
    </div>
  );
};

/**
 * Helper function to check if a product has an active offer
 * Can be used to conditionally show offer badges
 */
export const isProductOfferActive = (product) => {
  if (!product.has_category_offer || !product.selling_price) return false;
  if (!product.offer_valid_until) return true;
  
  const now = new Date();
  const endDate = new Date(product.offer_valid_until);
  return endDate > now;
};

/**
 * CategoryOfferBadge Component
 * Shows a badge when product has an active category offer
 */
export const CategoryOfferBadge = ({ product, lang = "ar" }) => {
  const [_] = useTranslation("global");
  
  if (!isProductOfferActive(product)) return null;
  
  return (
    <div className="category-offer-badge">
      <span className="offer-icon">🔥</span>
      <span>{_("home.categoryOffer")}</span>
    </div>
  );
};

export default ProductPriceDisplay;
